import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SupportLayout from './SupportLayout'
import { useAuth } from '../../hooks/useAuth'
import { usePractice } from '../../hooks/usePractice'
import { useToast } from '../../components/Toast'
import { supabase, getSettings } from '../../lib/supabase'
import usePageTitle from '../../hooks/usePageTitle'

// ─── SupportContact ──────────────────────────────────────────────────────────
//
// Contact form for logged-in users. Same Edge Function as the public contact
// form (`type: 'contact_form'`), but with `source: 'support'` so the inbox
// can be filtered/triaged separately. We also pass the user's email,
// practice name, and user ID along so we don't have to ask for context we
// already know.
//
// No honeypot or time-trap here because the route is auth-gated, so bots
// can't reach it.

export default function SupportContact() {
  const { user }                        = useAuth()
  const { practiceId }                  = usePractice() || {}
  const toast                           = useToast()
  usePageTitle('Contact support')

  const [practiceName, setPracticeName] = useState('')
  const [subject, setSubject]           = useState('')
  const [message, setMessage]           = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent]             = useState(false)
  const [error, setError]           = useState('')

  // Fetch the practice name from bookkeeper_settings so we can show it on the
  // "Signed in as" line and include it in the email metadata. Best-effort:
  // if it fails we just skip showing the practice name.
  useEffect(() => {
    if (!practiceId) return
    getSettings(practiceId).then(({ data }) => {
      if (data?.practice_name) setPracticeName(data.practice_name)
    }).catch(() => { /* non-fatal */ })
  }, [practiceId])

  useEffect(() => { window.scrollTo(0, 0) }, [])

  function validate() {
    if (!subject.trim()) return 'Please add a brief subject so we know what your message is about.'
    if (!message.trim()) return 'Please add a message.'
    if (message.trim().length < 10) {
      return 'Your message is a bit short. A few more details will help us help you.'
    }
    return null
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    setSubmitting(true)
    try {
      // Compose a message with context so the inbox gets a single tidy email
      // rather than us having to look up who sent it.
      const contextLines = [
        `Subject: ${subject.trim()}`,
        practiceName ? `Practice: ${practiceName}` : null,
        `User ID: ${user?.id || 'unknown'}`,
      ].filter(Boolean).join('\n')

      const composedMessage = `${contextLines}\n\n---\n\n${message.trim()}`

      const { error: invokeError } = await supabase.functions.invoke('send-email', {
        body: {
          type:    'contact_form',
          source:  'support',
          name:    (practiceName || user?.email || 'A customer').slice(0, 100),
          email:   (user?.email || '').slice(0, 200),
          firm:    (practiceName || '').slice(0, 150),
          message: composedMessage.slice(0, 6000),
        },
      })
      if (invokeError) throw invokeError

      setSent(true)
      toast("Message sent. We'll be in touch soon.", 'success')
    } catch (err) {
      console.error('Support contact error:', err)
      setError("Something went wrong sending your message. Please try again, or email us directly at hello@receiptchaser.com.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SupportLayout
      back={{ label: 'All support articles', to: '/support' }}
      title="Contact support"
      subtitle="Real humans, no chatbots. We'll get back to you shortly."
    >
      <div style={{
        background: 'var(--white)',
        border: '1px solid var(--surface-2)',
        borderRadius: 12,
        padding: 28,
        maxWidth: 720,
      }}>
        {sent ? (
          <SentState onSendAnother={() => {
            setSent(false)
            setSubject('')
            setMessage('')
          }} />
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {/* Signed-in-as line */}
            <div style={{
              background: 'var(--surface)',
              border: '1px solid var(--surface-2)',
              borderRadius: 8,
              padding: '12px 14px',
              marginBottom: 20,
              fontSize: '0.85rem',
              color: 'var(--ink-muted)',
              lineHeight: 1.55,
            }}>
              Signed in as <strong style={{ color: 'var(--ink)' }}>{user?.email}</strong>
              {practiceName && (
                <> at <strong style={{ color: 'var(--ink)' }}>{practiceName}</strong></>
              )}
              . We'll reply to this address.
            </div>

            <FormField label="What's this about?" htmlFor="subject">
              <input
                id="subject"
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                maxLength={150}
                placeholder="Short summary"
                style={inputStyle}
                disabled={submitting}
                required
              />
            </FormField>

            <FormField label="Your message" htmlFor="message">
              <textarea
                id="message"
                value={message}
                onChange={e => setMessage(e.target.value)}
                maxLength={5000}
                rows={8}
                placeholder="Tell us what's going on. If you're stuck on a specific step, mention which one. It helps us reply with a useful answer rather than a generic one."
                style={{ ...inputStyle, resize: 'vertical', minHeight: 160, fontFamily: 'inherit' }}
                disabled={submitting}
                required
              />
            </FormField>

            {error && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#991b1b',
                padding: '10px 14px',
                borderRadius: 8,
                fontSize: '0.88rem',
                marginBottom: 16,
                lineHeight: 1.5,
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
              style={{
                padding: '12px 28px',
                fontSize: '0.95rem',
                width: '100%',
                opacity: submitting ? 0.7 : 1,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? 'Sending…' : 'Send message →'}
            </button>
          </form>
        )}
      </div>

      {/* "Have you tried..." nudge: encourage self-service before asking */}
      <div style={{
        marginTop: 32,
        padding: 22,
        background: 'var(--white)',
        border: '1px dashed var(--surface-3)',
        borderRadius: 10,
        maxWidth: 720,
      }}>
        <h3 style={{
          margin: '0 0 8px',
          fontSize: '0.95rem',
          fontWeight: 600,
        }}>
          Before you send, you might find your answer in one of these:
        </h3>
        <ul style={{
          margin: 0, paddingLeft: 18,
          fontSize: '0.88rem', color: 'var(--ink-muted)',
          lineHeight: 1.7,
        }}>
          <li><Link to="/support/sending-your-first-request" style={linkStyle}>Sending your first receipt request</Link></li>
          <li><Link to="/support/csv-import-not-working" style={linkStyle}>CSV import isn't working</Link></li>
          <li><Link to="/support/client-didnt-get-email" style={linkStyle}>My client says they didn't get the email</Link></li>
          <li><Link to="/support/changing-or-cancelling-your-plan" style={linkStyle}>Changing or cancelling your plan</Link></li>
        </ul>
      </div>
    </SupportLayout>
  )
}

// ─── Bits ────────────────────────────────────────────────────────────────────

function FormField({ label, htmlFor, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label
        htmlFor={htmlFor}
        style={{
          display: 'block',
          fontSize: '0.85rem',
          fontWeight: 500,
          color: 'var(--ink)',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  )
}

function SentState({ onSendAnother }) {
  return (
    <div style={{ textAlign: 'center', padding: '24px 8px' }}>
      <div style={{
        width: 52, height: 52,
        borderRadius: '50%',
        background: 'var(--accent-light)',
        color: 'var(--accent)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.6rem',
        marginBottom: 18,
      }}>
        ✓
      </div>
      <h2 style={{
        fontSize: '1.3rem',
        marginBottom: 10,
        lineHeight: 1.3,
      }}>
        Got it, thanks.
      </h2>
      <p style={{
        color: 'var(--ink-muted)',
        fontSize: '0.95rem',
        lineHeight: 1.6,
        maxWidth: 420,
        margin: '0 auto 20px',
      }}>
        We'll get back to you shortly at the email address shown above.
      </p>
      <button
        onClick={onSendAnother}
        className="btn btn-secondary"
        style={{ padding: '9px 20px', fontSize: '0.88rem' }}
      >
        Send another message
      </button>
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  fontSize: '0.95rem',
  border: '1px solid var(--surface-2)',
  borderRadius: 8,
  background: 'var(--white)',
  color: 'var(--ink)',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  boxSizing: 'border-box',
}

const linkStyle = { color: 'var(--accent)', fontWeight: 500 }
